import React from 'react';
import {createPortal} from 'react-dom';
import {motion, AnimatePresence} from 'motion/react';
import {r} from "@/utils/responsive";
import masterBotTipAvatar from "@/assets/img/masterbot_avatar_tip.png"
import awardGradient from "@/assets/img/award_gradient.png"
import gridPattern from "@/assets/img/grid_pattern.svg"
import ButtonSpan from "@/components/ButtonSpan";
import ShortcutText from "@/components/ShortcutText";
import ButtonMain from "@/components/ButtonMain";
import buttonAwardNext from "@/assets/img/award_button.png"
import {TRANSITIONS} from "@/config/animations";
import type {ValidationError} from '@/stores/progressStore';
import ButtonReforge from "@/components/ButtonReforge";
import ModalBlurShapes from "@/components/ModalBlurShapes";
import ErrorMessageIcon from "@/assets/img/error_message.png"
import DisabledWrapper from "@/components/DisabledWrapper";
import ShortcutReforge from "@/components/ShortcutReforge";
import {useShortcuts} from "@/hooks/useShortcuts";
import OracleVideoAvatar from "@/assets/video/oracle_avatar_348x348.mp4"
import OracleAvatarStatic from "@/assets/img/hint_oracle_avatar_static.png"

interface ValidationErrorModalProps {
    isOpen: boolean;
    onClose: () => void;
    errors?: ValidationError[];
    errorCount: number;
    hint?: string[];
}

const ValidationErrorModal: React.FC<ValidationErrorModalProps> = ({
                                                                       isOpen,
                                                                       onClose,
                                                                       errorCount,
                                                                       hint,
                                                                       errors
                                                                   }) => {
    const showHint = errorCount >= 3 && hint && hint.length > 0;

    // При открытии модалки убираем фокус из Monaco Editor
    React.useEffect(() => {
        if (isOpen) {
            // Убираем фокус из Monaco Editor
            const activeElement = document.activeElement as HTMLElement;
            if (activeElement && activeElement.closest('.monaco-editor')) {
                activeElement.blur();
            }
            // Фокусируемся на body чтобы обработчики клавиш работали
            document.body.focus();
        }
    }, [isOpen]);

    useShortcuts({
        onEnter: onClose,
        disabled: !isOpen
    });

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{
                        backdropFilter: "blur(0px)",
                        backgroundColor: '#330000E5',
                    }}
                    animate={{
                        backdropFilter: "blur(0px)",
                        backgroundColor: '#330000E5',
                    }}
                    exit={{
                        backdropFilter: "blur(0px)",
                        backgroundColor: '#330000E5',
                    }}
                    transition={TRANSITIONS.slow}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 99999,
                        paddingLeft: r.vmax(63),
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        userSelect: "none",
                    }}
                >
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "start",
                        gap: r.vmax(16),
                    }}>
                        <motion.div
                            initial={{
                                opacity: 0,
                                y: 40,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                            exit={{
                                opacity: 0,
                                y: 40,
                            }}
                            transition={{
                                ...TRANSITIONS.slow,
                                delay: 0.2
                            }}
                            style={{
                                position: "relative",
                                aspectRatio: 863 / 888,
                                backgroundColor: '#7C0101',
                                width: r.vmax(863),
                                borderRadius: r.vmax(60),
                                paddingLeft: r.vmax(124),
                                paddingRight: r.vmax(124),
                                paddingTop: r.vmax(97),
                                paddingBottom: r.vmax(104),
                                overflowX: "hidden",
                            }}>
                            <div style={{
                                display: "flex", justifyContent: "center", position: "relative",
                                zIndex: 3
                            }}>
                                <div style={{
                                    position: 'relative',
                                    width: r.vmax(295),
                                    height: r.vmax(295),
                                }}>
                                    <DisabledWrapper style={{
                                        width: r.vmax(350),
                                        height: r.vmax(350),
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                    }}>
                                        <img src={ErrorMessageIcon} style={{
                                            width: '100%',
                                            height: '100%',
                                        }} alt=""/>
                                    </DisabledWrapper>
                                </div>
                            </div>
                            <div style={{
                                position: "relative",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                paddingTop: r.vmax(51),
                                paddingBottom: r.vmax(64),
                                color: 'white',
                                textAlign: 'center',
                                fontSize: r.vmax(48),
                                lineHeight: r.vmax(56),
                                zIndex: 3,
                            }}>
                                <h1>{`Задача решена неверно. \nПопробуйте еще раз`}</h1>
                            </div>
                            <div style={{
                                position: "relative",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                zIndex: 3
                            }}>
                                <div style={{
                                    width: r.vmax(568),
                                    height: r.vmax(110),
                                }}>
                                    <ButtonReforge enableGlowEffect={false} onClick={onClose} height={110}
                                                   minHeight={110} style={{
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        boxShadow: 'inset 0px -26px 34px rgba(255, 255, 255, 0.16), inset 0px 5px 50px rgba(255, 255, 255, 0.2)'
                                    }}>
                                        <ButtonSpan shadowColor="#FF5252">
                                            Продолжить
                                        </ButtonSpan>
                                        <ShortcutReforge isLarge shadowColor="#FF9C9C">ENTER</ShortcutReforge>
                                    </ButtonReforge>
                                </div>
                                {/*<ButtonMain width={568} height={110} glowWidth={648} glowHeight={190} glowTop={-30}*/}
                                {/*            glowImage={buttonAwardNext}*/}
                                {/*            onClick={onClose}>*/}
                                {/*    <ButtonSpan>Понятно</ButtonSpan>*/}
                                {/*    <ShortcutText text="ENTER"/>*/}
                                {/*</ButtonMain>*/}
                            </div>

                            <ModalBlurShapes shapeColor="#FF5353"/>
                            <div style={{
                                position: 'absolute',
                                inset: 0,
                                pointerEvents: 'none',
                                userSelect: 'none',
                                backgroundImage: `url(${gridPattern})`,
                                backgroundSize: r.vmax(101),
                                backgroundRepeat: 'repeat',
                                backgroundPosition: 'center center',
                                zIndex: 1,
                                mixBlendMode: 'color-dodge',
                                opacity: .3
                            }}>
                            </div>
                        </motion.div>

                        {showHint && (
                            <motion.div
                                initial={{
                                    opacity: 0,
                                    y: 40,
                                }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                }}
                                exit={{
                                    opacity: 0,
                                    y: 40,
                                }}
                                transition={{
                                    ...TRANSITIONS.slow,
                                    delay: 0.4
                                }}
                                style={{
                                    position: 'relative',
                                    backgroundColor: '#272E64',
                                    paddingLeft: r.vmax(32),
                                    paddingTop: r.vmax(32),
                                    paddingRight: r.vmax(32),
                                    paddingBottom: r.vmax(32),
                                    width: r.vmax(516),
                                    borderRadius: r.vmax(60),
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: r.vmax(32),
                                }}>
                                <div style={{
                                    position: 'relative',
                                    width: r.vmax(88),
                                    height: r.vmax(88),
                                    borderRadius: '50%',
                                    pointerEvents: 'none',
                                    userSelect: 'none',
                                    overflow: 'hidden',
                                    boxShadow: `0px 3.4321px 20.5926px #537EFF, 0px 3.4321px 46.3333px #537EFF, 0px 0px 8px #568EFF, 0px 10px 24px #5489FF, inset 0px -8.58025px 22.2228px -3.4321px #FFFFFF, inset 0px -11.1543px 27.4568px -23.1667px #5282FF, inset 0px -17.1605px 14.4148px -6.00617px #1741B1, inset 0px 4px 14px #5690FF`
                                }}>
                                    <div style={{
                                        width: r.vmax(181),
                                        height: r.vmax(181),
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)'
                                    }}>
                                        <img alt="" src={OracleAvatarStatic} style={{
                                            width: '100%',
                                            height: '100%',
                                        }}/>
                                    </div>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: r.vmax(16),
                                    color: 'white',
                                    fontSize: r.vmax(28),
                                    lineHeight: r.vmax(32),
                                    whiteSpace: 'pre-wrap',
                                }}>
                                    <p style={{
                                        marginBottom: r.vmax(4)
                                    }}>Подсказка от YAML Oracle:</p>
                                    {errors?.map((item, i) => (
                                        <p key={i}>{item.message}</p>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default ValidationErrorModal;
